import { http } from '@/core/api/';

// Coincide 1:1 con AIPromptConfigurationController.cs (EduEsp.Web.Api).
// NOTA: ese controlador está documentado ahí mismo como TEMPORAL (pensado originalmente
// para probar manualmente vía Postman/Swagger, US 156911) -- sigue siendo el único
// controlador real para esta capa, así que esta pantalla lo consume tal cual está hoy.
// Antes de que esto llegue a producción, hay que revisar con backend si conviene
// promoverlo a "definitivo" (ej. habilitar [Authorize], que hoy está comentado ahí).
const BaseUrl = 'api/aipromptconfiguration';

/**
 * Versión Publicada vigente para una clave (Módulo, Sección/Área).
 * Devuelve null (no 404) si no hay ninguna -- eso es un estado válido, no un error,
 * cuando se está creando la primera instrucción de una sección.
 */
export const getPublished = async (moduleName, sectionArea) => {
  try {
    const { data } = await http.get(`${BaseUrl}/published`, {
      params: { module: moduleName, sectionArea },
    });
    return data;
  } catch (e) {
    if (e?.response?.status === 404) {
      return null;
    }
    throw e;
  }
};

/** Todas las versiones (Borrador/Publicado/Archivado) de una clave, más reciente primero. */
export const getHistory = async (moduleName, sectionArea) => {
  const { data } = await http.get(`${BaseUrl}/history`, {
    params: { module: moduleName, sectionArea },
  });
  return data || [];
};

/** Catálogo completo de fuentes de contexto (CODE_AIPromptSource) para armar el checklist. */
export const getFuentesCatalogo = async () => {
  const { data } = await http.get(`${BaseUrl}/sources`);
  return data || [];
};

/** Catálogo de Módulos (CODE_AIPromptModule) para el dropdown "Módulo". */
export const getModulosCatalogo = async () => {
  const { data } = await http.get(`${BaseUrl}/modules`);
  return data || [];
};

/**
 * Catálogo de Secciones/Áreas (CODE_AIPromptSection) para el dropdown "Sección/Área".
 * aiPromptModuleId es opcional -- si se omite, devuelve el catálogo completo.
 */
export const getSeccionesCatalogo = async (aiPromptModuleId) => {
  const { data } = await http.get(`${BaseUrl}/sections`, {
    params: { aiPromptModuleId: aiPromptModuleId || undefined },
  });
  return data || [];
};

/** Crea una nueva versión en Borrador. */
export const createDraft = async (payload) => {
  const { data } = await http.post(`${BaseUrl}`, payload);
  return data;
};

/**
 * Actualiza EN SITIO un borrador existente (mismo Id, mismo Version -- no crea una fila
 * nueva). Solo funciona mientras la fila siga en estado Borrador; el backend devuelve 400 si
 * ya está Publicada/Archivada (en ese caso hay que usar createDraft para ramificar una versión
 * nueva). Agregado el 25 ago 2026 para poder iterar un borrador sin inflar el historial de
 * versiones antes de publicar -- ver AIPromptConfigurationEngine.UpdateDraftAsync.
 */
export const updateDraft = async (id, payload) => {
  const { data } = await http.put(`${BaseUrl}/${id}`, payload);
  return data;
};

/** Publica una versión en Borrador (archiva automáticamente la Publicada anterior, si existe). */
export const publishConfiguration = async (id) => {
  const { data } = await http.post(`${BaseUrl}/${id}/publish`);
  return data;
};

/** Archiva una versión directamente, sin pasar por Publicado. */
export const archiveConfiguration = async (id) => {
  const { data } = await http.post(`${BaseUrl}/${id}/archive`);
  return data;
};

/**
 * Métricas de uso/feedback de una configuración puntual (LOG_AiGeneration/
 * LOG_AiGenerationFeedback/LOG_AiGenerationAppliedField), para la tarjeta "Insights de
 * Feedback". Devuelve null (no 404) si el Id no existe -- mismo criterio que getPublished.
 */
export const getInsights = async (id) => {
  try {
    const { data } = await http.get(`${BaseUrl}/${id}/insights`);
    return data;
  } catch (e) {
    if (e?.response?.status === 404) {
      return null;
    }
    throw e;
  }
};

/** Fundamento Normativo guardado para una configuración (una fila por pregunta). */
export const getRegulation = async (id) => {
  const { data } = await http.get(`${BaseUrl}/${id}/regulation`);
  return data || [];
};

/** Vuelve a consultar al RAG todas las preguntas normativas y devuelve lo guardado. */
export const refreshRegulation = async (id) => {
  const { data } = await http.post(`${BaseUrl}/${id}/regulation/refresh`);
  return data || [];
};

/** Resumen de uso de todo el catálogo de instrucciones, para el panel "PEI GPT — Panel de Uso". */
export const getUsageSummary = async () => {
  const { data } = await http.get(`${BaseUrl}/usage-summary`);
  return data || null;
};
